/**
 * What an AI provider's refusal says to a person, in ONE place (29 Oct 2026, production India).
 *
 * WHY THIS EXISTS
 * ---------------
 * Google refused one Ask AI call and the customer read "AI is not available right now": the same sentence a revoked
 * key, an exhausted account, a safety block, a plain 400 and a timeout all produced. Every screen mapped "the
 * provider threw" to its own generic sentence, so a Google rejection read as "our system failed".
 *
 * The API (apps/api/src/modules/ai-chat/providers/ai-failure.ts) now answers every AI failure with an `errorCode`
 * (one of the six below) and an `aiError` object: `{ errorCode, provider, status, reason, triedFallback,
 * fallbackProvider?, fallback? }`. Some endpoints keep their own top-level code (`er_snr_ai_unavailable`,
 * `provider_auth`, `provider_out_of_credit`) and carry the object NESTED under `aiError`. Both shapes are read here.
 *
 * WHAT A SCREEN SHOWS
 * -------------------
 *  - the SENTENCE is selected from the fixed tables below by code and by provider (an enum: gemini / openai), then
 *    translated. It names the provider and says whether the other one was tried. Nothing in it is typed by a server.
 *  - the REASON is the provider's own words (one line, at most 300 characters, credentials already removed by the
 *    API). It is English, it is not ours to translate, and it is shown as typed text: `<UserText dir="ltr">`, under a
 *    label that says whose words they are (`<AiFailureReasons>`), or isolated left-to-right inside a plain string
 *    (`aiFailureText`, for toasts). It is present ONLY when the provider said something the API may repeat: when it
 *    did not (a reply that failed the API's JSON parse, a stalled socket) the API sends no `reason` key, and then no
 *    "in its own words" line is shown at all, only the sentence above (a missing, null, empty or non-string
 *    reason is no reason).
 *
 * Screens use `aiFailureOf(error)` to read the detail off whatever they caught, and either `<AiFailureNote>` /
 * `<AiFailureReasons>` or `aiFailureSentence` / `aiFailureText`. Anything already shown through `AiError`
 * (`lib/ai/errors.ts`) gets it for free: `AiError.failure` carries the detail and `resolveAiHeadline` speaks it.
 */
import { formatMessage } from '@/lib/i18n/format-message'

export type AiFailureTranslate = (key: string, params?: Record<string, string | number>) => string

export const AI_FAILURE_CODES = [
  'ai_provider_rejected',
  'ai_content_blocked',
  'ai_key_invalid',
  'ai_quota_exhausted',
  'ai_provider_unavailable',
  'ai_bad_output',
] as const
export type AiFailureCode = (typeof AI_FAILURE_CODES)[number]

// Every provider the API names in an aiError (canonical ids). Claude is a backup since r13 (1 Nov 2026); a name not
// here is read as none (providerOf), so no display map below is ever indexed with a string it does not hold.
export const AI_FAILURE_PROVIDERS = ['gemini', 'openai', 'anthropic'] as const
export type AiFailureProvider = (typeof AI_FAILURE_PROVIDERS)[number]

/** The other provider's answer, when it was tried. */
export interface AiFailureFallback {
  provider: AiFailureProvider | null
  errorCode: AiFailureCode
  status: number | null
  reason: string
}

/** An AI failure as the web reads it. */
export interface AiFailure {
  errorCode: AiFailureCode
  /** The provider that gave the headline answer; null when the response named none (or one we do not know). */
  provider: AiFailureProvider | null
  status: number | null
  /** The provider's own words, one line. '' when there are none (the API omits the key); then no reason line is shown. */
  reason: string
  /** True / false when the response says; null when it does not (a bare code), so nothing is claimed. */
  triedFallback: boolean | null
  fallbackProvider: AiFailureProvider | null
  fallback: AiFailureFallback | null
}

export function isAiFailureCode(value: unknown): value is AiFailureCode {
  return typeof value === 'string' && (AI_FAILURE_CODES as readonly string[]).includes(value)
}

function providerOf(value: unknown): AiFailureProvider | null {
  if (typeof value !== 'string') return null
  const lower = value.trim().toLowerCase()
  return (AI_FAILURE_PROVIDERS as readonly string[]).includes(lower) ? (lower as AiFailureProvider) : null
}

function statusOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** The API cuts a reason at 300 characters; this is a second belt, not the rule. */
const REASON_MAX_CHARS = 500

function reasonOf(value: unknown): string {
  if (typeof value !== 'string') return ''
  const flat = value.replace(/\s+/g, ' ').trim()
  const chars = [...flat]
  return chars.length > REASON_MAX_CHARS ? `${chars.slice(0, REASON_MAX_CHARS - 1).join('')}…` : flat
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

/**
 * The AI failure in a response body (or an SSE error frame), or null when the body is not one.
 *  - `aiError` with a known code and a provider: the full detail (this also covers `er_snr_ai_unavailable`,
 *    `provider_auth`, `provider_out_of_credit` and the email-template 400, which keep their own top-level code);
 *  - else a bare `errorCode` / `code` that is one of the six: a failure that names no provider and does not say
 *    whether another was tried;
 *  - else null.
 */
export function readAiFailure(body: unknown): AiFailure | null {
  const rec = record(body)
  if (!rec) return null

  const detail = record(rec.aiError)
  if (detail && isAiFailureCode(detail.errorCode) && typeof detail.provider === 'string') {
    const other = record(detail.fallback)
    const fallback: AiFailureFallback | null =
      other && isAiFailureCode(other.errorCode)
        ? {
            provider: providerOf(other.provider),
            errorCode: other.errorCode,
            status: statusOf(other.status),
            reason: reasonOf(other.reason),
          }
        : null
    return {
      errorCode: detail.errorCode,
      provider: providerOf(detail.provider),
      status: statusOf(detail.status),
      reason: reasonOf(detail.reason),
      triedFallback: detail.triedFallback === true,
      fallbackProvider: providerOf(detail.fallbackProvider) ?? fallback?.provider ?? null,
      fallback,
    }
  }

  const code = [rec.errorCode, rec.code].find(isAiFailureCode)
  if (code) return bareAiFailure(code)
  return null
}

/** A failure known only by its code (a session row, an old response): no provider, no claim about a fallback. */
export function bareAiFailure(errorCode: AiFailureCode): AiFailure {
  return { errorCode, provider: null, status: null, reason: '', triedFallback: null, fallbackProvider: null, fallback: null }
}

function isAiFailure(value: unknown): value is AiFailure {
  const rec = record(value)
  return !!rec && isAiFailureCode(rec.errorCode) && 'provider' in rec && 'triedFallback' in rec
}

/**
 * The AI failure in whatever an AI call threw: an `AiError` (`.failure`), the `ApiClientError` `fetchApi` throws
 * (`.body`), the `ErApiError` `erFetch` throws (`.data`), or a bare response body. Structural on purpose: importing
 * the fetch stack here would drag it into every AI surface. Null when the throw is not an AI failure.
 */
export function aiFailureOf(error: unknown): AiFailure | null {
  const rec = record(error)
  if (!rec) return null
  if (isAiFailure(rec.failure)) return rec.failure
  for (const body of [rec.body, rec.data, rec]) {
    const failure = readAiFailure(body)
    if (failure) return failure
  }
  return null
}

// ── the words ────────────────────────────────────────────────────────────────

/** Each provider's name in the catalogue. Exported so every screen names a provider from this one map. */
export const AI_FAILURE_PROVIDER_NAME_KEYS: Readonly<Record<AiFailureProvider, string>> = {
  gemini: 'aiFailure.provider.gemini',
  openai: 'aiFailure.provider.openai',
  anthropic: 'aiFailure.provider.anthropic',
}

const HEADLINE_KEYS: Record<AiFailureCode, string> = {
  ai_provider_rejected: 'aiFailure.headline.ai_provider_rejected',
  ai_content_blocked: 'aiFailure.headline.ai_content_blocked',
  ai_key_invalid: 'aiFailure.headline.ai_key_invalid',
  ai_quota_exhausted: 'aiFailure.headline.ai_quota_exhausted',
  ai_provider_unavailable: 'aiFailure.headline.ai_provider_unavailable',
  ai_bad_output: 'aiFailure.headline.ai_bad_output',
}

const GENERIC_KEYS: Record<AiFailureCode, string> = {
  ai_provider_rejected: 'aiFailure.generic.ai_provider_rejected',
  ai_content_blocked: 'aiFailure.generic.ai_content_blocked',
  ai_key_invalid: 'aiFailure.generic.ai_key_invalid',
  ai_quota_exhausted: 'aiFailure.generic.ai_quota_exhausted',
  ai_provider_unavailable: 'aiFailure.generic.ai_provider_unavailable',
  ai_bad_output: 'aiFailure.generic.ai_bad_output',
}

const TRIED_KEYS: Record<AiFailureCode, string> = {
  ai_provider_rejected: 'aiFailure.tried.ai_provider_rejected',
  ai_content_blocked: 'aiFailure.tried.ai_content_blocked',
  ai_key_invalid: 'aiFailure.tried.ai_key_invalid',
  ai_quota_exhausted: 'aiFailure.tried.ai_quota_exhausted',
  ai_provider_unavailable: 'aiFailure.tried.ai_provider_unavailable',
  ai_bad_output: 'aiFailure.tried.ai_bad_output',
}

/**
 * The English rung: every `aiFailure.*` sentence. `messages/en.json` carries the same text (a guard pins the two),
 * and `AiError.headline` (English, for the callers that have no translator) is built from this table.
 */
export const AI_FAILURE_COPY: Readonly<Record<string, string>> = {
  'aiFailure.provider.gemini': "Google's AI (Gemini)",
  'aiFailure.provider.openai': 'OpenAI',
  'aiFailure.provider.anthropic': "Anthropic's AI (Claude)",

  'aiFailure.headline.ai_provider_rejected': '{provider} declined this request.',
  'aiFailure.headline.ai_content_blocked': 'The safety filter of {provider} blocked this request. Rephrase it and try again.',
  'aiFailure.headline.ai_key_invalid': 'The AI key for {provider} is not valid. An admin can replace it in Settings → AI Settings.',
  'aiFailure.headline.ai_quota_exhausted':
    'The quota for {provider} is used up. An admin can add credit with the provider or replace the key in Settings → AI Settings.',
  'aiFailure.headline.ai_provider_unavailable': '{provider} is not answering right now. Try again in a minute.',
  'aiFailure.headline.ai_bad_output': 'The answer from {provider} could not be used. Try again.',

  'aiFailure.generic.ai_provider_rejected': 'The AI provider declined this request.',
  'aiFailure.generic.ai_content_blocked': 'The safety filter of the AI provider blocked this request. Rephrase it and try again.',
  'aiFailure.generic.ai_key_invalid': 'The AI key is not valid. An admin can replace it in Settings → AI Settings.',
  'aiFailure.generic.ai_quota_exhausted':
    'The quota of the AI provider is used up. An admin can add credit with the provider or replace the key in Settings → AI Settings.',
  'aiFailure.generic.ai_provider_unavailable': 'The AI provider is not answering right now. Try again in a minute.',
  'aiFailure.generic.ai_bad_output': 'The AI answer could not be used. Try again.',

  'aiFailure.tried.ai_provider_rejected': 'We also tried {provider}, which declined it too.',
  'aiFailure.tried.ai_content_blocked': 'We also tried {provider}, whose safety filter blocked it too.',
  'aiFailure.tried.ai_key_invalid': 'We also tried {provider}, but its key is not valid.',
  'aiFailure.tried.ai_quota_exhausted': 'We also tried {provider}, but its quota is used up.',
  'aiFailure.tried.ai_provider_unavailable': 'We also tried {provider}, but it is not answering right now.',
  'aiFailure.tried.ai_bad_output': 'We also tried {provider}, but its answer could not be used.',
  'aiFailure.tried.generic': 'We also tried {provider}.',
  'aiFailure.tried.unnamed': 'We also tried the other AI provider.',
  'aiFailure.tried.none': 'No other AI provider was tried for this request.',

  'aiFailure.reason.named': 'Reason given by {provider}, in its own words:',
  'aiFailure.reason.unnamed': 'Reason given by the AI provider, in its own words:',
}

const englishTranslate: AiFailureTranslate = (key, params) => formatMessage(AI_FAILURE_COPY[key] ?? key, params)

/** One reason a provider gave, labelled with whose words they are. */
export interface AiFailureReason {
  provider: AiFailureProvider | null
  label: string
  /** The provider's words, verbatim: typed text, not ours. */
  text: string
}

export interface AiFailureParts {
  /** What happened and to which provider: "Google's AI (Gemini) declined this request." */
  headline: string
  /** Whether the other provider was tried and what it answered; null when the response does not say. */
  tried: string | null
  /** The provider's words (and the other provider's), each with its label. Empty when there are none. */
  reasons: AiFailureReason[]
}

export function describeAiFailure(failure: AiFailure, t: AiFailureTranslate): AiFailureParts {
  const name = (provider: AiFailureProvider | null): string | null => (provider ? t(AI_FAILURE_PROVIDER_NAME_KEYS[provider]) : null)

  const headlineName = name(failure.provider)
  const headline = headlineName
    ? t(HEADLINE_KEYS[failure.errorCode], { provider: headlineName })
    : t(GENERIC_KEYS[failure.errorCode])

  let tried: string | null = null
  if (failure.triedFallback === false) {
    tried = t('aiFailure.tried.none')
  } else if (failure.triedFallback === true) {
    const otherName = name(failure.fallbackProvider)
    if (!otherName) tried = t('aiFailure.tried.unnamed')
    else if (failure.fallback) tried = t(TRIED_KEYS[failure.fallback.errorCode], { provider: otherName })
    else tried = t('aiFailure.tried.generic', { provider: otherName })
  }

  const labelFor = (provider: AiFailureProvider | null): string => {
    const label = name(provider)
    return label ? t('aiFailure.reason.named', { provider: label }) : t('aiFailure.reason.unnamed')
  }
  const reasons: AiFailureReason[] = []
  if (failure.reason) reasons.push({ provider: failure.provider, label: labelFor(failure.provider), text: failure.reason })
  if (failure.fallback?.reason) {
    reasons.push({ provider: failure.fallback.provider, label: labelFor(failure.fallback.provider), text: failure.fallback.reason })
  }

  return { headline, tried, reasons }
}

/** The sentence a screen leads with: what happened to which provider, and whether the other one was tried. */
export function aiFailureSentence(failure: AiFailure, t: AiFailureTranslate): string {
  const { headline, tried } = describeAiFailure(failure, t)
  return tried ? `${headline} ${tried}` : headline
}

// Unicode left-to-right isolate and pop-directional isolate: they keep the provider's English words in order inside
// an Arabic sentence (a toast cannot carry a <bdi>).
const LRI = String.fromCharCode(0x2066)
const PDI = String.fromCharCode(0x2069)

/**
 * The whole failure as one plain string, for a toast or an inline message that cannot render the reason as its own
 * element: the sentence, then each reason under its label, the provider's words isolated left to right.
 */
export function aiFailureText(failure: AiFailure, t: AiFailureTranslate): string {
  const parts = describeAiFailure(failure, t)
  const lead = parts.tried ? `${parts.headline} ${parts.tried}` : parts.headline
  return [lead, ...parts.reasons.map((reason) => `${reason.label} ${LRI}${reason.text}${PDI}`)].join(' ')
}

/** The English sentence, for the callers that have no translator (`AiError.headline`, `Error.message`). */
export function englishAiFailureSentence(failure: AiFailure): string {
  return aiFailureSentence(failure, englishTranslate)
}

/** Every catalogue key the failure words use, for the guard that pins the four catalogues. */
export const AI_FAILURE_KEYS: readonly string[] = Object.keys(AI_FAILURE_COPY)

/**
 * The provider-named text for whatever an AI call threw, or null when it was not an AI failure. For a toast or an
 * inline sentence that keeps its own wording for every other failure:
 * `toast.error(aiFailureMessage(err, t) ?? t(refusalKey(err)))`.
 */
export function aiFailureMessage(error: unknown, t: AiFailureTranslate): string | null {
  const failure = aiFailureOf(error)
  return failure ? aiFailureText(failure, t) : null
}
